import {AfterViewInit, Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {Ofwca} from '../../../shared/interface/ofwca';
import {NgForm} from '@angular/forms';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {OfwcaService} from '../../../shared/services/ofwca.service';
import {delay, distinctUntilChanged, first, takeUntil, tap} from 'rxjs/operators';
import {CountedAndPaginated} from '../../../shared/interface/response/counted-and-paginated';
import {ConfirmDialogComponent} from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatDialog} from '@angular/material/dialog';
import {UserPermissions} from '../../../shared/interface/user-permissions';
import {AuthService} from '../../../shared/services/auth.service';
import {Uuid} from '../../../shared/interface/response/uuid';
import {JobResultDialogComponent} from '../../../shared/components/job-result-dialog/job-result-dialog.component';
import {HttpErrorResponse} from '@angular/common/http';
import {ActivatedRoute, Router} from '@angular/router';
import {FilterService} from '../../../shared/services/filter.service';
import {select, Store} from '@ngrx/store';
import {StoreRootState} from '../../../shared/ngrx/reducers';
import {getCurrentRouteState} from '../../../shared/ngrx/selector';
import {RouterStateUrl} from '../../../shared/ngrx/route-serializer';
import {ReportCustomComponent} from "../../report-custom/report-custom.component";
import {REPORT_TYPE_OFWCA, REPORT_TYPE_OFWCA_REGIONAL} from "../../../shared/services/dict.service";
import {Sort} from "@angular/material/sort";
import {
  ChangeRegistrationDialogComponent
} from "../../receivables/imports-row/change-registration-dialog/change-registration-dialog.component";
import {
  OfwcaRejectedCommissionAddComponent
} from "../ofwca-rejected-commission-add/ofwca-rejected-commission-add.component";

@Component({
  selector: 'app-all-ofwcas-list',
  templateUrl: './all-ofwcas-list.component.html',
  styleUrls: ['./all-ofwcas-list.component.scss']
})
export class AllOfwcasListComponent implements OnDestroy, AfterViewInit {
  @Input() displayedColumns: string[] = null;
  @Input() rejectedCommission?: number = null;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  initialFilters: any;
  ofwcas: Ofwca[] = [];
  totalData = 0;
  // displayedColumns: string[] = ['fullName', 'status', 'pesel', 'email', 'phone', 'facility', 'partner', 'assetsRegional', 'actions', ];
  userPermissions: ReplaySubject<UserPermissions>;
  sortBy = 'fullNameReversed';
  sortOrder = 'ASC';

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private ofwcaService: OfwcaService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.ofwcaService.getOfwcaListSubject()
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<Ofwca>) => {
          this.ofwcas = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();
  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      recordId: 'string',
      eq$profitNumber: 'string',
      contains$firstName: 'string',
      contains$secondName: 'string',
      contains$lastName: 'string',
      contains$pesel: 'string',
      contains$documentNumber: 'string',
      email: 'string',
      contains$phone: 'string',
      address: 'string',
      state: 'int',
      assetsRegional: 'string[]',
      lifeRegional: 'string[]',
      partnerAssetsRegional: 'string[]',
      partner: 'string',
      facilityAddress: 'string',
      facilityState: 'int',
      owner: 'int',
      verified: 'int',
      partnerTag: 'int[]',
      rauNumber: 'string',
      status: 'int[]',
      active: 'int',
      hiperioAutomaticAccessUpdate: 'int',
      hiperioGrantingAccess: 'int',
      signedContractWithTheEmployee: 'int',
      contains$familyName: 'string',
      has360Account: 'int',
    }, filters);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.activatedRoute.data
      .pipe(
        delay(0),
        first()
      )
      .subscribe((data: any) => {
        this.rejectedCommission = this.rejectedCommission ?? data.rejectedCommission ?? null;
        this.displayedColumns = this.displayedColumns ?? data.displayedColumns ?? ['fullName', 'status', 'pesel',
          'email', 'phone', 'facility', 'partner', 'assetsRegional', 'actions' ];
      });

    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe((event: PageEvent) => {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          ...this.filtersForm.value
        })]);
      });

    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$),
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.sortBy = state.params?.sortBy ? state.params.sortBy : 'fullNameReversed';
        this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'asc';
        this.loadListData();
      })
    ;
  }

  filter(): void {
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
      page: 0,
      pageSize: this.paginator.pageSize,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      ...this.filtersForm.value,
    })]);
  }

  loadListData(): void {
    this.ofwcaService.loadList(this.paginator.pageIndex,
      this.paginator.pageSize,
      this.buildFilters(),
      null,
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      });
  }

  buildFilters(headers = false): any {
    const filters = {...this.initialFilters};

    if (null !== this.rejectedCommission) {
      filters.rejectedCommission = this.rejectedCommission;
    }

    if (headers){
      filters.headers = 'Imię,Nazwisko,RAU,Email produktowy,Email konkursowy,' +
        'Zgoda na wiadomości konkursowe,Zgoda na wiadomości produktowe,Partner,Status,' +
        'Nazwa Partnera do analiz,Pełny adres placówki,IDD konkursy,Partner Nadprowizja,Właściciel';
    }

    return filters;
  }

  downloadReport(): void {
    this.ofwcaService.downloadReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }
  downloadReportRegional(): void {
    this.ofwcaService.downloadCustomReportRegional(this.buildFilters(true))
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  downloadRejectedCommissionReport(): void {
    this.ofwcaService.downloadRejectedCommissionReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  generateSurgeReport(): void {
    this.ofwcaService.downloadSurgeReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  generateSignedContractWithTheEmployeeReport(): void {
    this.ofwcaService.downloadSignedContractWithTheEmployeeReport(this.buildFilters())
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
      page: 0,
      pageSize: this.paginator.pageSize,
    }]);
  }

  deleteOfwca(ofwca: Ofwca): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć OFWCA?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.ofwcaService.deleteOfwca(ofwca.uuid)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto OFWCA', '', {duration: 2000});
              this.loadListData();
            },
            error: (response: HttpErrorResponse) => {
              this.snack.open(response.error.error.text, response.status.toString(), {duration: 2000});
            }
          });
      }
    });
  }

  deleteRejectedCommission(ofwca: Ofwca): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {text: 'Czy na pewno chcesz usunąć OFWCA?'},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.ofwcaService.ofwcaRejectedCommission(ofwca.uuid, 0)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto OFWCA', '', {duration: 2000});
              this.loadListData();
            },
            error: (response: HttpErrorResponse) => {
              this.snack.open(response.error.error.text, response.status.toString(), {duration: 2000});
            }
          });
      }
    });
  }

  openReportDialog(): void {
    const dialogRef = this.dialog.open(ReportCustomComponent, {
      width: '70%',
      data: {filter: this.filtersForm,
        reportName: REPORT_TYPE_OFWCA},
    });
  }

  openReportDialogRegional(): void {
    const dialogRef = this.dialog.open(ReportCustomComponent, {
      width: '70%',
      data: {filter: this.filtersForm,
        reportName: REPORT_TYPE_OFWCA_REGIONAL},
    });
  }

  sortChanged($event: Sort): void {
    this.sortBy = $event.active;
    this.sortOrder = $event.direction;
    this.loadListData();
  }

  addRejectedCommission(): void {
    const dialogRef = this.dialog.open(OfwcaRejectedCommissionAddComponent, {
      width: '700px',
    });

    dialogRef.afterClosed().subscribe(() => {
     this.loadListData();
    });
  }
}
